import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPBPalette, getEmbedInfo, getReadingTime, stripHtml, PB } from "../utils/pbBlogTheme";
import {
  FiClock, FiShare2, FiHeart, FiMessageCircle, FiArrowLeft,
  FiCheck, FiSend, FiFilm, FiPlay
} from "react-icons/fi";
import Nav from '../components/nav';

const BASE_URL = "https://employeeserverpilo.onrender.com";

function getVisitorId() {
  let id = localStorage.getItem("pb_visitor_id");
  if (!id) { id = "v_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("pb_visitor_id", id); }
  return id;
}

const REACTIONS = [
  { type: "like", emoji: "👍" }, { type: "love", emoji: "❤️" },
  { type: "clap", emoji: "👏" }, { type: "insightful", emoji: "💡" },
];

// ---------------------------------------------------------------------------
// Bouncing-dots loader
// ---------------------------------------------------------------------------
const DotsLoader = ({ label = "Rolling film" }) => (
  <div style={{ minHeight: "70vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, background: PB.bg }}>
    <div style={{ display: "flex", gap: 9 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 13, height: 13, borderRadius: "50%",
          background: i === 0 ? PB.gold : i === 1 ? PB.violet : "#fb7185",
          animation: `pbBounce 1.2s ${i * 0.15}s infinite ease-in-out`,
        }} />
      ))}
    </div>
    <span style={{ fontSize: 13, color: PB.textDim, letterSpacing: "0.05em" }}>{label}…</span>
    <style>{`@keyframes pbBounce{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-13px);opacity:1}}`}</style>
  </div>
);

// ---------------------------------------------------------------------------
// Scroll-reveal
// ---------------------------------------------------------------------------
const Reveal = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); o.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(26px)",
      transition: `opacity .7s cubic-bezier(.16,1,.3,1) ${delay}ms, transform .7s cubic-bezier(.16,1,.3,1) ${delay}ms`,
    }}>{children}</div>
  );
};

// ---------------------------------------------------------------------------
// Scroll progress bar (signature) — a thin film-gold reel timeline at the top
// ---------------------------------------------------------------------------
const ScrollProgress = ({ accent }) => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setPct(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 3, zIndex: 60, background: "transparent" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: accent, transition: "width .1s linear", boxShadow: `0 0 10px ${accent}` }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Embed renderer — handles every aspect ratio
// ---------------------------------------------------------------------------
const EmbedBlock = ({ url }) => {
  const info = getEmbedInfo(url);
  if (!info) return null;

  if (info.type === "link") {
    return (
      <a href={info.embedUrl} target="_blank" rel="noopener noreferrer" style={{
        display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderRadius: 12,
        background: PB.bgRaised, border: `1px solid ${PB.border}`, color: PB.gold,
        fontSize: 14, fontWeight: 600, textDecoration: "none", margin: "24px 0", wordBreak: "break-all",
      }}><FiPlay size={15} /> {url}</a>
    );
  }

  const narrow = info.aspect === "9/16" || info.aspect === "4/5";
  return (
    <div style={{
      margin: "8px 0", borderRadius: 16, overflow: "hidden",
      boxShadow: "0 12px 50px rgba(0,0,0,0.5)", border: `1px solid ${PB.border}`,
      aspectRatio: info.aspect.replace("/", " / "),
      maxWidth: narrow ? 380 : "100%",
      marginLeft: narrow ? "auto" : 0, marginRight: narrow ? "auto" : 0,
    }}>
      <iframe src={info.embedUrl} loading="lazy" style={{ width: "100%", height: "100%", border: "none" }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen title="Embedded media" />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Share
// ---------------------------------------------------------------------------
const ShareButton = ({ accent }) => {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ url, title: document.title }); return; } catch {} }
    await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={share} style={{
      display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10,
      border: `1px solid ${copied ? "#10b981" : PB.border}`, background: PB.bgRaised, cursor: "pointer",
      fontSize: 13, fontWeight: 600, color: copied ? "#10b981" : PB.text, transition: "all .2s",
    }}>
      {copied ? <FiCheck size={14} /> : <FiShare2 size={14} />} {copied ? "Link copied" : "Share"}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Reactions
// ---------------------------------------------------------------------------
const ReactionBar = ({ blogId, accent }) => {
  const [summary, setSummary] = useState({ total: 0, by_type: {}, user_reacted: null });
  const visitorId = getVisitorId();
  const load = async () => {
    try { const r = await fetch(`${BASE_URL}/api/v1/blog/${blogId}/reactions?user_id=${visitorId}`); setSummary(await r.json()); } catch {}
  };
  useEffect(() => { load(); }, [blogId]); // eslint-disable-line
  const react = async (type) => {
    const off = summary.user_reacted === type;
    try {
      if (off) await fetch(`${BASE_URL}/api/v1/blog/${blogId}/react?user_id=${visitorId}`, { method: "DELETE" });
      else await fetch(`${BASE_URL}/api/v1/blog/${blogId}/react`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reaction_type: type, user_id: visitorId, user_type: "public" }),
      });
      load();
    } catch {}
  };
  return (
    <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
      {REACTIONS.map(r => {
        const active = summary.user_reacted === r.type;
        const count = summary.by_type?.[r.type] || 0;
        return (
          <button key={r.type} onClick={() => react(r.type)} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "8px 15px", borderRadius: 24,
            border: `1px solid ${active ? accent : PB.border}`,
            background: active ? `${accent}1a` : PB.bgRaised, cursor: "pointer",
            fontSize: 14, fontWeight: 600, color: active ? accent : PB.text, transition: "all .2s",
          }}>
            <span style={{ fontSize: 16 }}>{r.emoji}</span>
            {count > 0 && <span style={{ fontSize: 12 }}>{count}</span>}
          </button>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------
const CommentsSection = ({ blogId, accent }) => {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const load = async () => {
    try { const r = await fetch(`${BASE_URL}/api/v1/blog/${blogId}/comments?page=1&page_size=50`); const d = await r.json(); setComments(d.comments || []); } catch {}
  };
  useEffect(() => { load(); }, [blogId]); // eslint-disable-line
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || content.trim().length < 2) return;
    setPosting(true);
    try {
      await fetch(`${BASE_URL}/api/v1/blog/${blogId}/comments`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, author_name: name, author_type: "public" }),
      });
      setContent(""); load();
    } catch {} finally { setPosting(false); }
  };
  const inp = {
    width: "100%", padding: "11px 15px", borderRadius: 10, fontSize: 14,
    border: `1px solid ${PB.border}`, background: PB.bgInput, color: PB.text,
    outline: "none", boxSizing: "border-box",
  };
  return (
    <div style={{ marginTop: 52 }}>
      <h3 style={{ fontSize: 21, fontWeight: 800, marginBottom: 22, color: PB.text, display: "flex", alignItems: "center", gap: 9, fontFamily: "'Georgia',serif" }}>
        <FiMessageCircle /> Join the conversation {comments.length > 0 && <span style={{ color: PB.textDim, fontWeight: 600 }}>({comments.length})</span>}
      </h3>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 11, marginBottom: 30 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" maxLength={50} style={inp} />
        <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Share your take..." rows={3} maxLength={1000} style={{ ...inp, resize: "vertical" }} />
        <button type="submit" disabled={posting || !name.trim() || content.trim().length < 2} style={{
          alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, padding: "10px 22px",
          borderRadius: 10, border: "none", background: accent, color: "#0a0a0f", fontWeight: 800, fontSize: 13,
          cursor: "pointer", opacity: posting || !name.trim() || content.trim().length < 2 ? 0.45 : 1,
        }}><FiSend size={13} /> {posting ? "Posting…" : "Post comment"}</button>
      </form>
      {comments.length === 0 ? (
        <p style={{ color: PB.textDim, fontSize: 14 }}>No comments yet. Start the discussion.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {comments.map(c => (
            <div key={c.id} style={{ padding: "16px 18px", borderRadius: 12, background: PB.bgRaised, border: `1px solid ${PB.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                <strong style={{ fontSize: 13, color: PB.text }}>{c.author_name}</strong>
                <span style={{ fontSize: 11, color: PB.textDim }}>{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14, color: "#c5c5d2", lineHeight: 1.6 }}>{c.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN — PBNewsArticle (route: /producerbuddy/news/:slug)
// ═══════════════════════════════════════════════════════════════════════════
const PBNewsArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true); setError(false);
    fetch(`${BASE_URL}/api/v1/blog/${slug}`)
      .then(r => { if (!r.ok) throw new Error("404"); return r.json(); })
      .then(setPost).catch(() => setError(true)).finally(() => setLoading(false));
  }, [slug]);

  // ── SEO: inject document title + meta tags (client-side, helps social/AI crawlers) ──
  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    document.title = `${post.meta_title || post.title} | ProducerBuddy`;

    const setMeta = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
      return el;
    };
    const desc = post.meta_description || stripHtml(post.excerpt).slice(0, 160);
    const cover = post.cover_image_id ? `${BASE_URL}/api/v1/blog/image/${post.cover_image_id}` : "";
    const created = [
      setMeta("name", "description", desc),
      setMeta("property", "og:title", post.meta_title || post.title),
      setMeta("property", "og:description", desc),
      setMeta("property", "og:type", "article"),
      setMeta("property", "og:url", window.location.href),
      ...(cover ? [setMeta("property", "og:image", cover)] : []),
      setMeta("name", "twitter:card", cover ? "summary_large_image" : "summary"),
    ];

    // JSON-LD structured data for rich indexing
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: post.title, description: desc,
      author: { "@type": "Organization", name: "ProducerBuddy" },
      publisher: { "@type": "Organization", name: "ProducerBuddy", alternateName: "Producer Buddy" },
      datePublished: post.published_at || post.created_at,
      ...(cover && { image: cover }),
      mainEntityOfPage: window.location.href,
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      ld.remove();
      // leave meta tags; they'll be overwritten by next article
    };
  }, [post]);

  if (loading) return <DotsLoader />;

  if (error || !post) {
    return (
      <div style={{ minHeight: "70vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: PB.bg, color: PB.text, textAlign: "center", padding: "0 20px" }}>
        <FiFilm size={40} style={{ color: PB.gold, opacity: 0.5, marginBottom: 16 }} />
        <h2 style={{ fontFamily: "'Georgia',serif" }}>This scene didn't make the cut</h2>
        <p style={{ color: PB.textDim, marginBottom: 24 }}>The story you're looking for isn't here.</p>
        <button onClick={() => navigate("/news")} style={{
          padding: "11px 26px", borderRadius: 10, border: "none",
          background: `linear-gradient(135deg,${PB.gold},${PB.violet})`, color: "#0a0a0f",
          fontWeight: 800, cursor: "pointer",
        }}>Back to all stories</button>
      </div>
    );
  }

  const pal = getPBPalette(post.id);
  const mins = getReadingTime(post.content);
  const cover = post.cover_image_id ? `${BASE_URL}/api/v1/blog/image/${post.cover_image_id}` : null;

  return (
    <>
    <Nav />
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", background: PB.bg, minHeight: "100vh", color: PB.text }}>
      <ScrollProgress accent={pal.accent} />

      {/* Hero */}
      <div style={{
        position: "relative", minHeight: cover ? 420 : 300,
        background: cover ? `center/cover url(${cover})` : PB.bg,
        display: "flex", alignItems: "flex-end", overflow: "hidden",
        paddingTop: 100, boxSizing: "border-box",
      }}>
        {/* ambient glow for text-only hero */}
        {!cover && (
          <div aria-hidden="true" style={{
            position: "absolute", top: "-20%", right: "-5%", width: 480, height: 480, borderRadius: "50%",
            background: `radial-gradient(circle, ${pal.glow}, transparent 70%)`, filter: "blur(30px)",
          }} />
        )}
        {/* film-grain + vignette overlay */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0,
          background: cover
            ? "linear-gradient(to top, rgba(10,10,15,0.96) 5%, rgba(10,10,15,0.4) 50%, rgba(10,10,15,0.6))"
            : `linear-gradient(to top, ${PB.bg}, transparent 70%)`,
        }} />

        <div style={{ position: "relative", maxWidth: 800, margin: "0 auto", padding: "0 20px 40px", width: "100%" }}>
          <button onClick={() => navigate("/news")} style={{
            display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20,
            background: "rgba(255,255,255,0.08)", border: `1px solid ${PB.border}`, borderRadius: 20,
            padding: "7px 15px", color: PB.text, fontSize: 12, fontWeight: 600, cursor: "pointer", backdropFilter: "blur(8px)",
          }}><FiArrowLeft size={12} /> All stories</button>

          {post.tags?.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {post.tags.map(t => (
                <span key={t} style={{
                  fontSize: 11, fontWeight: 700, padding: "5px 13px", borderRadius: 6,
                  background: "rgba(0,0,0,0.45)", color: pal.accent, border: `1px solid ${pal.accent}55`,
                  textTransform: "uppercase", letterSpacing: "0.08em", backdropFilter: "blur(6px)",
                }}>{t}</span>
              ))}
            </div>
          )}

          <h1 style={{
            margin: 0, fontSize: "clamp(28px,5.5vw,52px)", fontWeight: 900,
            fontFamily: "'Georgia',serif", lineHeight: 1.12, letterSpacing: "-0.02em",
            color: "#fff", textShadow: "0 2px 30px rgba(0,0,0,0.6)", wordBreak: "break-word",
          }}>{post.title}</h1>
        </div>
      </div>

      {/* Meta bar (no author name shown) */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "22px 20px 0" }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12, paddingBottom: 24, borderBottom: `1px solid ${PB.border}`,
        }}>
          <span style={{ fontSize: 13, color: PB.textDim, display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
            <FiClock size={13} /> {mins} min read · {new Date(post.published_at || post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </span>
          <ShareButton accent={pal.accent} />
        </div>
      </div>

      {/* Body */}
      <article style={{ maxWidth: 800, margin: "0 auto", padding: "36px 20px" }}>
        <Reveal>
          <div className="pb-content" style={{ fontSize: 18, lineHeight: 1.9, color: "#d4d4dd", fontFamily: "'Georgia','Times New Roman',serif" }}
            dangerouslySetInnerHTML={{ __html: post.content }} />
        </Reveal>

        {post.video_url && (
          <Reveal delay={100}>
            <div style={{ marginTop: 40 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 18 }}>
                <div style={{ width: 4, height: 24, borderRadius: 2, background: pal.grad }} />
                <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: PB.text, fontFamily: "'Georgia',serif", display: "flex", alignItems: "center", gap: 8 }}>
                  <FiPlay size={16} style={{ color: pal.accent }} /> Watch
                </h3>
              </div>
              <EmbedBlock url={post.video_url} />
            </div>
          </Reveal>
        )}

        <Reveal delay={150}>
          <div style={{ marginTop: 48, paddingTop: 28, borderTop: `1px solid ${PB.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
              <FiHeart style={{ color: pal.accent }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: PB.text }}>Enjoyed this story?</span>
            </div>
            <ReactionBar blogId={post.id} accent={pal.accent} />
          </div>
        </Reveal>

        <Reveal delay={200}>
          <CommentsSection blogId={post.id} accent={pal.accent} />
        </Reveal>
      </article>

      <style>{`
        .pb-content h1{font-size:1.9em;font-weight:800;margin:1.2em 0 .5em;color:${PB.text};font-family:'Georgia',serif}
        .pb-content h2{font-size:1.55em;font-weight:700;margin:1.2em 0 .5em;color:${PB.text};font-family:'Georgia',serif}
        .pb-content h3{font-size:1.28em;font-weight:700;margin:1em 0 .5em;color:${PB.text}}
        .pb-content p{margin:0 0 1.2em}
        .pb-content blockquote{border-left:3px solid ${pal.accent};margin:1.6em 0;padding:.6em 1.4em;background:${pal.glow.replace('0.3','0.08').replace('0.35','0.08').replace('0.32','0.08').replace('0.28','0.08')};font-style:italic;border-radius:0 8px 8px 0;color:${PB.text}}
        .pb-content ul,.pb-content ol{padding-left:28px;margin-bottom:1.2em}
        .pb-content li{margin-bottom:.5em}
        .pb-content a{color:${pal.accent};text-decoration:underline;text-underline-offset:3px}
        .pb-content img{max-width:100%;height:auto;border-radius:12px;margin:1.4em 0;border:1px solid ${PB.border}}
        .pb-content strong{font-weight:800;color:${PB.text}}
        @media (prefers-reduced-motion: reduce){*{animation:none!important;transition:none!important}}
      `}</style>
    </div>
    </>
  );
};

export default PBNewsArticle;